export interface StructuredQuery {
  keywords: string[]
  requiredPhrases: string[]
  requiredPatterns: RegExp[]
}

export interface MatchableData {
  dataString: string
  dataArray: string[]
}

/**
 * Converts a query like this: `a "query" WITH "Some Required" text 123`
 * into
 * keywords: ['a', 'query', 'with', 'some', 'required', 'text', '123']
 * requiredPhrases: ['query', 'some required']
 *
 * This is to decompose a query string into something we can use for matching against data. Specifically, this allows
 * us to parse a query string for double quotes, giving us required phrases to match against. Extracting keywords and
 * required phrases out of the query string allows us to later compare against a data string, and match _every_ required
 * phrase, and _some_ keywords.
 */
export function toStructuredQuery(query: string): StructuredQuery {
  // Sanitise query - only has lowered alphanumeric, double quotes and whitespace
  const loweredQuery = query.toLowerCase().replace(/[^a-z0-9" ]/g, '')

  // Treat everything as keywords - entry should loosely contain one of these
  const keywords = loweredQuery
    .replace(/"/g, '')
    .split(' ')
    .filter((keyword) => keyword !== '')
  // Pull out a list of required phrases for exact matching - `entry should "include this exactly" and "this too"`
  const requiredPhrases = loweredQuery.match(/"[a-z0-9 ]*"/g)?.map((str) => str.replace(/"/g, '').trim()) ?? []
  // Regex form of the phrase, that we can test a data string against via pattern.test(dataString)
  // Will match the phrase as long as it is surrounded by whitespace or at start/end of the datastring
  const requiredPatterns = requiredPhrases.map((phrase) => new RegExp(`(^| )${phrase}( |$)`))

  return { keywords, requiredPhrases, requiredPatterns }
}

/**
 * Converts/sanitises an of array of data fields like ['my title', 'my "DeScripTION"', 'some daTa']
 * into
 * dataString: 'my title my description some data'
 * dataArray: ['my title', 'my description', 'some data']
 *
 * This is to prepare it for matching against.
 */
export function toMatchableData(dataFields: string[]): MatchableData {
  const dataArray = dataFields.map((field) => field.toLowerCase().replace(/[^0-9a-z ]/g, ''))
  const dataString = dataArray.join(' ')
  return { dataString, dataArray }
}

export function matchItem(matchableData: MatchableData, structuredQuery: StructuredQuery) {
  const { dataString } = matchableData
  const { keywords, requiredPatterns } = structuredQuery
  return (
    requiredPatterns.every((pattern) => pattern.test(dataString)) &&
    keywords.some((keyword) => dataString.includes(keyword))
  )
}
